import {createSelector, createSlice} from "@reduxjs/toolkit";
import { formatDate, parseDate } from "../utils/dateFuncs";

const initialState = {
    dates: {
        ops: {
            startDate: `${new Date().getFullYear()}-01-01`,
            endDate: `${new Date().getFullYear()}-12-31`,
        },
        docs: {
            startDate: `${new Date().getFullYear()}-01-01`,
            endDate: `${new Date().getFullYear()}-12-31`,
        }
    },
    bankAccount: null,
    bankAccounts: [],
    pageTitle: '',
    updater: false,
    isLoading: false,
    optional:{
        ops: {
            searchTerms: '',
            minAmount: '',
            maxAmount: '',
        },
        docs: {
            searchTerms: '',
            minAmount: '',
            maxAmount: '',
        }
    }
}

export const filtersSlice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        setStartDate(state, action) {
            if (action.payload.validity) {
                state.dates[action.payload.target].startDate = action.payload.value;
            } else {
                //Si la date n'est pas valide alors mettre à vide
                state.dates.startDate = '';
            }
        },
        setEndDate(state, action) {
            if (action.payload.validity) {
                state.dates[action.payload.target].endDate = action.payload.value;
            } else {
                //Si la date n'est pas valide alors mettre à vide
                state.dates.endDate = '';
            }
        },
        setMyBankAccount(state, action) {
            state.bankAccount = action.payload
        },
        //Tableau comptes bancaires
        setBankAccounts(state, action) {
            state.bankAccounts = action.payload
        },
        addBankInList(state, action) {
            if (!state.bankAccounts.some(ba => ba.id === action.payload.id)) {
                state.bankAccounts.push(action.payload)
                if (state.bankAccount === null) {
                    state.bankAccount = state.bankAccounts[0]
                }
            }
        },
        setPageTitle(state, action) {
            state.pageTitle = action.payload
        },
        updateDate(state, action) {
            state.updater = !state.updater
        },
        clearDates(state, action) {
            if (action.payload?.target) {
                state.dates[action.payload.target].startDate = '';
                state.dates[action.payload.target].endDate = '';
            } else {
                // Clear all target filter dates
                Object.keys(state.dates).forEach((target => {
                    state.dates[target].startDate = '';
                    state.dates[target].endDate = '';
                }));
            }
        },
        resetDates(state, action) {
            if (action.payload?.target) {
                state.dates[action.payload.target].startDate = initialState.dates[action.payload.target].startDate;
                state.dates[action.payload.target].endDate = initialState.dates[action.payload.target].endDate;
            } else {
                // Reset all target filter dates
                Object.keys(state.dates).forEach((target => {
                    state.dates[target].startDate = initialState.dates[target].startDate;
                    state.dates[target].endDate = initialState.dates[target].endDate;
                }));
            } 
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload
        },
        setOptional(state, action) {
            state.optional[action.payload.target] = action.payload.values;
        },
        clearOptional(state, action) {
            state.optional[action.payload.target] = {
                searchTerms: '',
                minAmount: '',
                maxAmount: '',
            }
        }
    },
})

export const {
    setStartDate,
    setEndDate,
    setMyBankAccount,
    setBankAccounts,
    addBankInList,
    setPageTitle,
    updateDate,
    clearDates,
    resetDates,
    setIsLoading,
    setMinAmount,
    setMaxAmount,
    setOptional,
    clearOptional
} = filtersSlice.actions;

export default filtersSlice.reducer;

//Liste des dépendances utilisées par les createSelector ci-dessous.
const selectStartDate = (state, target) => state.filters.dates[target].startDate;
const selectEndDate = (state, target) => state.filters.dates[target].endDate;
const updater = state => state.filters.updater;
// CreateSelector permet d'avoir un selector qui ne se rafraichi que si une ou plusieurs des ses dépandences changent.

/**
 * @param target - If the target is docs return null for the dates, it the target is ops return the dates of the current year
 * @param {"date"|"string"} format - Desired return format
 */
export const createDateFilterSelector = (target, format = 'date') => createSelector(
    (state) => selectStartDate(state, target),
    (state) => selectEndDate(state, target),
    (startDateStr, endDateStr) => {
        const currentYear = new Date().getFullYear();

        const defaultStart = target == 'ops' ? new Date(currentYear, 0, 1) : null;
        const defaultEnd = target == 'ops' ? new Date(currentYear, 11, 31) : null;

        const startDate = parseDate(startDateStr, defaultStart);
        const endDate = parseDate(endDateStr, defaultEnd);

        if (format === 'string') {
            return [
                startDate ? formatDate(startDate) : null,
                endDate ? formatDate(endDate) : null
            ];
        }

        return [startDate, endDate];
    }
);

// For raw Date objects, return nulls if not present
export const getRawDatesFilter = (target) => createDateFilterSelector(target, 'date');

// For string format with default values (start/end of current year)
export const getStringDatesFilter = (target) => createDateFilterSelector(target, 'string');

export function getBankAccount(state) {
    return state.filters.bankAccount
}

export function getFilters(state) {
    return state.filters
}

export function getBankAccounts(state) {
    return state.filters.bankAccounts
}

export function getSearchTerms(state) {
    return state.filters.searchTerms
}

export function getIsLoading(state) {
    return state.filters.isLoading;
}

export function getMinAmount(state) {
    return state.filters.minAmount
}

export function getMaxAmount(state) {
    return state.filters.maxAmount
}

export function getOptional(state, target = false) {
    // UNCLEAN getter: The code below returns a new object (new ref) everytime, which causes unnecessary remounts (Always memoize it in the target components)
    // return an object that contain all optional properties that are not empty
    let optional = {};
    let source = {};
    
    if (target) {
        source = state.filters.optional[target];
    } else {
        source = state.filters.optional;
    }

    Object.entries(source).forEach(([key, value]) => {
        if (value !== '') {
            optional[key] = value;
        }
    });

    return optional;
}

export function getTrueOptional(state) {
    return state.filters.optional;
}
